// Minimal focus trap implementation. When `active`, Tab and Shift+Tab cycle
// focus inside the container's focusable descendants; on activation the
// previously-focused element is remembered and restored on deactivation.

import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
  'select:not([disabled]),textarea:not([disabled]),iframe,object,embed,' +
  '[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    function getFocusable(): HTMLElement[] {
      if (!container) return [];
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    }

    // Move focus into the container if it isn't already there.
    if (!container.contains(document.activeElement)) {
      const focusables = getFocusable();
      (focusables[0] ?? container).focus();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const focusables = getFocusable();
      if (focusables.length === 0) {
        event.preventDefault();
        container?.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (activeEl === first || !container?.contains(activeEl)) {
          event.preventDefault();
          last?.focus();
        }
      } else {
        if (activeEl === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active, containerRef]);
}
